import { useState, useEffect } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectIsAdmin } from "../../store/authSelectors";
import {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} from "../../store/slices/apiSlice";
import { useToast } from "./use-toast";
import { CreateLicenseData, EditLicenseData, License } from "../../store/types";
import { getErrorMessage } from "./use-getError";

export const useLicensesAPI = () => {
  const isAdmin = useAppSelector(selectIsAdmin);
  const licensesEP = useAppSelector((state) => state.endpoints.licenses);
  const userLicensesEP = useAppSelector(
    (state) => state.endpoints.userLicenses
  );
  const [licenses, setLicenses] = useState<License[] | null>(null);
  const { toastRef, showSuccess, showWarning, showError } = useToast();

  const [addItem, { isLoading: isAdding }] = useAddMutation();
  const [editItem, { isLoading: isEditing }] = useUpdateMutation();
  const [deleteItem, { isLoading: isDeleting }] = useRemoveMutation();

  const {
    data: fetchedLicenses,
    refetch: refetchLicenses,
    isLoading: isFetchLoading,
  } = useGetQuery(licensesEP, {
    refetchOnMountOrArgChange: true,
    skip: !licensesEP || !isAdmin,
  });

  const refetchData = async () => {
    await refetchLicenses();
  };

  const handleError = (operation: string, error: any) => {
    showError(
      "Ошибка",
      `Не удалось ${operation}, ошибка: ${error}. Попробуйте позже.`
    );
  };

  const handlePostLicense = async (
    license: CreateLicenseData
  ): Promise<{ success: boolean; licenses?: License[] }> => {
    try {
      const result = await addItem({
        endpoint: licensesEP,
        id: "",
        body: license,
      });
      if (result.data) {
        showSuccess("Успешно", "Лицензия создана");
        const licenses = result.data;
        return { success: true, licenses };
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("создать лицензию", serverError ?? result.error);
      return { success: false };
    } catch (error) {
      handleError("создать лицензию", error);
      return { success: false };
    }
  };

  const handleActivateLicense = async (id: string): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: licensesEP,
        id: `${id}/enable`,
      });
      if (result.data) {
        showSuccess("Успешно", "Лицензия активирована");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("активировать лицензию", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("активировать лицензию", error);
      return false;
    }
  };

  const handleDeactivateLicense = async (id: string): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: licensesEP,
        id: `${id}/disable`,
      });
      if (result.data) {
        showSuccess("Успешно", "Лицензия деактивирована");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("деактивировать лицензию", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("деактивировать лицензию", error);
      return false;
    }
  };

  const handleUpdateLicense = async (
    license: EditLicenseData,
    licenseId: string
  ): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: licensesEP,
        id: licenseId,
        body: license,
      });
      if (result.data) {
        showSuccess("Успешно", "Данные лицензии обновлены");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("обновить лицензию", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("обновить лицензию", error);
      return false;
    }
  };

  const handleUpdateLicenseUser = async (
    id: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: licensesEP,
        id: `${id}/user/${userId}`,
      });
      if (result.data) {
        showSuccess("Успешно", "Пользователь лицензии обновлен");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError(
        "обновить пользователя лицензии",
        serverError ?? result.error
      );
      return false;
    } catch (error) {
      handleError("обновить пользователя лицензии", error);
      return false;
    }
  };

  const handleDeleteLicenseUser = async (id: string): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: `${licensesEP}/${id}/user`,
        id: "",
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError(
          "удалить пользователя из лицензии",
          serverError ?? result.error
        );
        return false;
      }

      showSuccess("Успешно", "Пользователь удален из лицензии");
      return true;
    } catch (error) {
      handleError("удалить пользователя из лицензии", error);
      return false;
    }
  };

  const handleDeleteLicense = async (license: License): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: `${licensesEP}/${license?.id}`,
        id: "",
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError("удалить лицензию", serverError ?? result.error);
        return false;
      }

      showSuccess("Успешно", "Лицензия удалена");
      return true;
    } catch (error) {
      handleError("удалить лицензию", error);
      return false;
    }
  };

  const handleClearHardware = async (
    userId: string,
    projectId: string
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: `${userLicensesEP}/${userId}/licenses/${projectId}/reset`,
        id: "",
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError(
          "сбросить информацию об оборудовании",
          serverError ?? result.error
        );
        return false;
      }

      showSuccess("Успешно", "Информация об оборудовании сброшена");
      return true;
    } catch (error) {
      handleError("сбросить информацию об оборудовании", error);
      return false;
    }
  };

  const handlePostSessionMember = async (
    licenseId: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: `${licensesEP}/${licenseId}/session/members/${userId}`,
        id: "",
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError(
          "добавить пользователя в сессию",
          serverError ?? result.error
        );
        return false;
      }

      showSuccess("Успешно", "Пользователь добавлен в сессию.");
      return true;
    } catch (error) {
      handleError("добавить пользователя в сессию", error);
      return false;
    }
  };

  const handleDeleteSessionMember = async (
    licenseId: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: `${licensesEP}/${licenseId}/session/members/${userId}`,
        id: "",
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError(
          "удалить пользователя из сессии",
          serverError ?? result.error
        );
        return false;
      }

      showSuccess("Успешно", "Пользователь удален из сессии.");
      return true;
    } catch (error) {
      handleError("удалить пользователя из сессии", error);
      return false;
    }
  };

  const handleEditMaxActiveUsers = async (
    licenseId: string,
    maxActiveUsers: number
  ): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: `${licensesEP}/${licenseId}/session`,
        id: "",
        body: {
          maxActiveUsers: maxActiveUsers,
        },
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError(
          "изменить количество активных пользователей",
          serverError ?? result.error
        );
        return false;
      }

      showSuccess("Успешно", "Количество активных пользователей изменено.");
      return true;
    } catch (error) {
      handleError("изменить количество активных пользователей", error);
      return false;
    }
  };

  useEffect(() => {
    if (fetchedLicenses) setLicenses(fetchedLicenses);
  }, [fetchedLicenses]);

  return {
    licensesEP,
    isFetchLoading,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleUpdateLicenseUser,
    handleDeleteLicenseUser,
    handleDeleteLicense,
    handleClearHardware,
    handlePostSessionMember,
    handleDeleteSessionMember,
    handleEditMaxActiveUsers,
    refetchData,
    licenses,
    isAdding,
    isEditing,
    isDeleting,
    toastRef,
    showSuccess,
    showError,
    showWarning,
    handleError,
  };
};
